import { useState } from 'react'
import { Tag, Plus, Save, Trash2, RefreshCw } from 'lucide-react'
import { getAdminPlans, adminSavePlan, adminDeletePlan } from '../../services/api'
import {
  C, Card, Row, Btn, Loading, ErrorLine, Empty, inputStyle, selectStyle, labelStyle,
  useAsync, fmtMoney, Pill, divider, confirmAsync,
} from './adminUI'

// Раздел «Тарифы»: редактор subscription_plans — единственный источник цен и
// названий для экрана «Подписка» у юзера. code — первичный ключ (UPSERT):
// сохранение с существующим кодом обновляет тариф, с новым — создаёт.
// Удаление тарифа не рушит историю: в оплаченных заявках plan_code хранится
// строкой, выручка по нему остаётся в статистике.

const EMPTY = { code: '', title_ru: '', type: 'real', months: 1, price_usdt: 0, discount: '', old_price_usdt: '' }

export default function PlansSection() {
  const { data, loading, error, reload } = useAsync(() => getAdminPlans(), [])
  const [form, setForm] = useState(null)   // null = форма закрыта
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')

  const plans = data?.plans || []
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form) return
    setBusy(true); setNote('')
    try {
      await adminSavePlan({
        code: String(form.code || '').trim(),
        title_ru: String(form.title_ru || '').trim(),
        type: form.type === 'demo' ? 'demo' : 'real',
        months: Number(form.months) || 0,
        price_usdt: Number(form.price_usdt) || 0,
        discount: String(form.discount || '').trim() || null,
        old_price_usdt: (form.old_price_usdt == null || form.old_price_usdt === '')
          ? null
          : Number(form.old_price_usdt),
      })
      setNote(`Тариф ${form.code} сохранён`)
      setForm(null)
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy(false) }
  }

  const remove = async (code) => {
    if (!(await confirmAsync(`Удалить тариф ${code}? Он исчезнет с экрана подписки у юзеров.`))) return
    setBusy(true); setNote('')
    try {
      await adminDeletePlan(code)
      setNote(`Тариф ${code} удалён`)
      if (form?.code === code) setForm(null)
      reload()
    } catch (e) { setNote(`Ошибка: ${e?.message || e}`) }
    finally { setBusy(false) }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Btn icon={Plus} onClick={() => { setForm({ ...EMPTY }); setNote('') }}>Новый тариф</Btn>
        <div style={{ flex: 1 }} />
        <Btn small ghost icon={RefreshCw} color={C.muted} onClick={reload}>Обновить</Btn>
      </div>

      <ErrorLine onRetry={reload}>{error}</ErrorLine>
      {note && (
        <div style={{ fontSize: 12, color: note.startsWith('Ошибка') ? C.danger : C.accent, marginBottom: 10 }}>
          {note}
        </div>
      )}

      {form && (
        <Card title={plans.some(p => p.code === form.code) ? `Тариф ${form.code}` : 'Новый тариф'} icon={Tag}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Код (латиница, без пробелов)</div>
              <input value={form.code} onChange={e => set('code', e.target.value)} placeholder="m1" style={inputStyle} />
            </div>
            <div style={{ width: 110 }}>
              <div style={labelStyle}>Тип</div>
              <select value={form.type} onChange={e => set('type', e.target.value)} style={selectStyle}>
                <option value="real">real</option>
                <option value="demo">demo</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={labelStyle}>Название (видит юзер)</div>
            <input value={form.title_ru} onChange={e => set('title_ru', e.target.value)} placeholder="1 месяц" style={inputStyle} />
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Месяцев</div>
              <input type="number" min="0" value={form.months} onChange={e => set('months', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Цена, USDT</div>
              <input type="number" min="0" step="0.01" value={form.price_usdt} onChange={e => set('price_usdt', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={labelStyle}>Бейдж</div>
              <input value={form.discount ?? ''} onChange={e => set('discount', e.target.value)} placeholder="-20%" style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={labelStyle}>Старая цена, USDT (зачёркнутая на экране подписки)</div>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.old_price_usdt ?? ''}
              onChange={e => set('old_price_usdt', e.target.value)}
              placeholder="—"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <Btn icon={Save} disabled={busy || !form.code.trim() || !form.title_ru.trim()} onClick={save}>
              {busy ? '…' : 'Сохранить'}
            </Btn>
            <Btn ghost color={C.muted} onClick={() => setForm(null)}>Отмена</Btn>
          </div>
          <div style={{ fontSize: 10.5, color: C.dim, marginTop: 10, lineHeight: 1.45 }}>
            Код — это ключ: сохранение с уже существующим кодом перезапишет тариф.
            months = 0 подходит для бессрочного/тестового доступа.
          </div>
        </Card>
      )}

      {loading && !plans.length ? <Loading /> : !plans.length ? <Empty>Тарифов нет</Empty> : (
        <Card title={`Тарифы (${plans.length})`} icon={Tag}>
          {plans.map((p, i) => (
            <div key={p.code} style={{ borderTop: i ? `1px solid ${C.line}` : 'none', paddingTop: i ? 10 : 0, marginTop: i ? 10 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: '#fff' }}>{p.title_ru}</div>
                {p.discount && <Pill color={C.warn}>{p.discount}</Pill>}
                <Pill color={p.type === 'demo' ? C.indigo : C.accent}>{p.type}</Pill>
              </div>
              <Row label="Код" value={p.code} mono />
              <Row label="Цена" value={`${fmtMoney(p.price_usdt)} USDT`} />
              <Row label="Старая цена" value={p.old_price_usdt ? `${fmtMoney(p.old_price_usdt)} USDT` : '—'} />
              <Row label="Срок" value={p.months ? `${p.months} мес.` : 'без срока'} />
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <Btn small ghost onClick={() => { setForm({ ...EMPTY, ...p, discount: p.discount || '' }); setNote('') }}>Изменить</Btn>
                <Btn small ghost color={C.danger} icon={Trash2} disabled={busy} onClick={() => remove(p.code)}>Удалить</Btn>
              </div>
            </div>
          ))}
          <div style={divider} />
          <div style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.45 }}>
            Эти же тарифы показываются юзеру в окне оплаты и используются ботом
            при выдаче подписки.
          </div>
        </Card>
      )}
    </div>
  )
}
